import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { InviteUserRequest } from '../../core/services/user/admin-users.service';
import { UserRole } from '../../core/models/user';
import { SelectComponent } from '../../ui';

/** Invite-user modal (AC8): email + role + optional welcome note. */
@Component({
  selector: 'app-invite-user-dialog',
  standalone: true,
  imports: [FormsModule, SelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dialog-header">
      <h2>Invite user</h2>
      <button class="drawer-close" type="button" aria-label="Close" (click)="ref.close()">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          width="18"
          height="18"
        >
          <path d="M6 6l12 12M6 18L18 6" />
        </svg>
      </button>
    </div>

    <form (ngSubmit)="submit()">
      <div class="dialog-body">
        <div class="form-group">
          <label class="form-label" for="invite-email">Email</label>
          <input
            id="invite-email"
            class="form-input"
            type="email"
            name="email"
            placeholder="name@medone.com"
            [(ngModel)]="email"
            required
            autocomplete="off"
          />
        </div>

        <div class="form-group">
          <label class="form-label" for="invite-first-name">First name</label>
          <input
            id="invite-first-name"
            class="form-input"
            type="text"
            name="firstName"
            placeholder="First name"
            [(ngModel)]="firstName"
            required
            autocomplete="off"
          />
        </div>

        <div class="form-group">
          <label class="form-label" for="invite-last-name">Last name</label>
          <input
            id="invite-last-name"
            class="form-input"
            type="text"
            name="lastName"
            placeholder="Last name"
            [(ngModel)]="lastName"
            required
            autocomplete="off"
          />
        </div>

        <div class="form-group">
          <label class="form-label" for="invite-role">Role</label>
          <app-select name="role" ariaLabel="Role" [options]="roleOptions" [(ngModel)]="role" />
        </div>

        <div class="form-group">
          <label class="form-label" for="invite-note">
            Welcome note <span style="color: var(--text-muted); font-weight: 400;">(optional)</span>
          </label>
          <textarea
            id="invite-note"
            class="form-textarea"
            name="welcomeNote"
            placeholder="Add a short note to include in the invitation email…"
            [(ngModel)]="welcomeNote"
          ></textarea>
        </div>
      </div>

      <div class="dialog-footer">
        <button class="btn btn-ghost" type="button" (click)="ref.close()">Cancel</button>
        <button
          class="btn btn-primary"
          type="submit"
          [disabled]="!email.trim() || !firstName.trim() || !lastName.trim()"
        >
          Send invite
        </button>
      </div>
    </form>
  `,
})
export class InviteUserDialogComponent {
  readonly ref = inject<DialogRef<InviteUserRequest, InviteUserDialogComponent>>(DialogRef);

  readonly roles: UserRole[] = ['Admin', 'Lead', 'Tester', 'Viewer'];
  readonly roleOptions = this.roles.map((r) => ({ value: r, label: r }));

  email = '';
  firstName = '';
  lastName = '';
  role: UserRole = 'Tester';
  welcomeNote = '';

  submit(): void {
    const email = this.email.trim();
    const firstName = this.firstName.trim();
    const lastName = this.lastName.trim();
    if (!email || !firstName || !lastName) return;
    this.ref.close({
      email,
      firstName,
      lastName,
      role: this.role,
      welcomeNote: this.welcomeNote.trim() || undefined,
    });
  }
}
